import React, { useEffect, useMemo, useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import LoadingView from '../../shared/components/LoadingView';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import {
  isAllowedAssetUrl,
  isAllowedReleaseLink,
  sanitizeReleaseHtml,
} from '../utils/sanitizeReleaseHtml';
import { API_URL } from '../../config/env';

const LAST_OPENED_KEY = 'admin_releases_last_opened_at';

export default function ReleasesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiUrl = `${API_URL}/v1/admin/feeds/releases`;

  useDocumentTitle('Releases – Maintella');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetchWithAuth(apiUrl);
        if (!res.ok) throw new Error('Failed to load releases');
        const json = await res.json();
        if (isMounted) setData(json);
      } catch {
        if (isMounted) setError('Could not load releases');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [apiUrl]);

  // Persist last opened timestamp when page becomes visible with data loaded
  useEffect(() => {
    if (!loading && data) {
      window.localStorage.setItem(LAST_OPENED_KEY, new Date().toISOString());
    }
  }, [loading, data]);

  const items = useMemo(() => {
    if (!data?.items) return [];
    return data.items.slice(0, 20);
  }, [data]);

  return (
    <>
      <ObjectTopBar title="Releases" />
      {/* Back on the documented `container py-4`. The cards' own `margin: 2rem 4rem` was what
          doubled this page's inset and squeezed a phone; with that gone the standard shell is
          the right gutter, and it is the same one the dashboard now uses. */}
      <div className="container py-4">
        {loading ? (
          <LoadingView
            symbolClass="bi bi-megaphone"
            title="Loading releases"
            subtitle="Fetching the latest product updates."
            className="py-5"
          />
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <div className="releases">
            {items.map((item) => {
              const releaseLink = isAllowedReleaseLink(item.link) ? item.link : null;
              const heroImageUrl = isAllowedAssetUrl(item.media?.url) ? item.media.url : null;
              const sanitizedContent = sanitizeReleaseHtml(item.content_encoded);

              return (
              <article key={item.guid || item.link || item.title} className="release-card">
                <header className="release-card-header d-flex justify-content-between align-items-start mb-2">
                  <h3 className="release-card-title h5 mb-0">
                    {releaseLink ? (
                      <a href={releaseLink} target="_blank" rel="noreferrer noopener" className="text-decoration-none text-dark">
                        {item.title}
                      </a>
                    ) : (
                      <span className="text-dark">{item.title}</span>
                    )}
                  </h3>
                  <div className="release-card-date text-muted small ms-3 whitespace-nowrap">
                    {formatDate(item.pubDate)}
                  </div>
                </header>

                {heroImageUrl && (
                  <figure className="release-card-hero mb-3">
                    <img
                      src={heroImageUrl}
                      alt={item.title || ''}
                    />
                  </figure>
                )}

                {sanitizedContent && (
                  <div
                    className="release-card-body content"
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                  />
                )}

                {releaseLink && (
                  <div className="release-card-footer mt-3">
                    <a href={releaseLink} target="_blank" rel="noreferrer noopener" className="btn btn-sm btn-outline-secondary">
                      Read more
                    </a>
                  </div>
                )}
              </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function getReleasesLastOpened() {
  return window.localStorage.getItem(LAST_OPENED_KEY);
}

